import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import format from 'string-template';

import {
  Checkbox,
  Switch,
  FormControlLabel,
  ToggleButton,
} from '@mui/material';
import {
  DatePicker,
  TimePicker,
} from '@mui/lab';
import {
  PostAdd,
  Business,
} from '@mui/icons-material';
import {
  IonItem,
  IonSpinner,
} from '@ionic/react';
import {
  combineDateAndTimeWithUTCOffset,
  isToday,
} from '../../../../../utils/date';
import { uploadMessageAttachments } from '../../../../../utils/attachments';
import { MAX_FILE_UPLOAD_SIZE, MAX_FILE_UPLOAD_SIZE_MB } from '../../../../../utils/files';

import Coach from '../../../../../Model/Coach';
import Product from '../../../../../Model/Product';
import ChatContext from '../../../../context/ChatContext';

import { storagePaths, pathPlaceholder } from '../../../../../utils/firebasePaths';
import { CoachingActivity } from '../../../../../utils/log';
import { getUTCOffset, roundToNext5Minutes } from '../../../../../utils/time';
import { getGivenFeatureEnabledUsersList } from '../../../../utils/user';
import { Features, isProductHasEnabledGivenFeature } from '../../../../utils/product';
import {
  isClientDisengaged,
  matchesChatSilenceSegment,
  isDisengagementEligible,
} from '../../../../utils/disengagement';
import { categoryMap } from '../../../../../utils/userCategory';
import useStorage from '../../../../../hooks/useStorage';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useLogger from '../../../../../hooks/useLogger';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useDisengagementThresholds from '../../../../hooks/useDisengagementThresholds';
import UserContext from '../../../../../context/UserContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import PodContext from '../../../../../context/PodContext';
import { ALL_CLIENTS } from '../../../../../context/PodContext/constants';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { filterUnsupportedFiles, getUnsupportedFileTypes } from '../../../../../utils/file';

import defaultUser from '../../assets/icon/default-user.svg';
import TabPanel from '../../../../components/TabPanel';
import AttachmentsContainer from '../../../../components/AttachmentsContainer';
import { OutreachType, DISENGAGEMENT_BROADCAST_TAG } from '../../../../constants/disengagementFilters';
import EmojiInput from '../EmojiInput';
import useToast from '../../../../hooks/useToast';
import DialogRoundedModal from '../../../../../components/DialogRoundedModal';
import AudioAttachmentsContainer from './components/AudioAttachmentsContainer';
import ScheduledMessagesList from './components/ScheduledBroadcasts';
import ExcludeTagsDrawer from './components/ExcludeTagsDrawer';
import ForceSendHelperText from './components/ForceSendHelperText';

import {
  StyledTabs,
  StyledTab,
  TabContainer,
  SectionContainer,
  SectionLabel,
  StyledToggleButtonGroup,
  SearchBar,
  SelectAllButton,
  StyledListContainer,
  RecipientRow,
  StyledAvatar,
  RecipientInfo,
  RecipientName,
  RecipientSubText,
  PersonalizeChipsContainer,
  PersonalizeChip,
  StyledTextField,
  MessageToolbar,
  AttachmentsWrapper,
  DateSelectorContainer,
  DateSelectorField,
  OptionsRow,
  HelperText,
  SendBroadcastButton,
  StyledAlert,
  ModalBody,
  ConflictWarning,
} from './styles';
import { DELIVERY_MODE, AVATAR_PIXEL_SIZE } from './constants';

import text from './text.json';

const filterTag = {
  ALL: 'ALL',
  POD_ALL_CLIENTS: 'All clients in this pod',
  SIX_PLUS_DAYS_WITHOUT_CHECK_INS: 'Clients without check-ins (6+ days)',
  POD_SIX_PLUS_DAYS_WITHOUT_CHECK_INS: 'Clients without check-ins (6+ days) in this pod',
  NO_CHAT_2_BUSINESS_DAYS: 'Clients without chats (2+ business days)',
  POD_NO_CHAT_2_BUSINESS_DAYS: 'Clients without chats (2+ business days) in this pod',
  ALL_DISENGAGED: 'Disengaged clients',
  POD_ALL_DISENGAGED: 'Disengaged clients in this pod',
};

// Disengagement filter tags used to detect targeted-outreach broadcasts
const disengagementFilterTags = [filterTag.ALL_DISENGAGED, filterTag.POD_ALL_DISENGAGED];

// Tags the server can only resolve when it also knows which pod was selected
const podScopedFilterTags = [
  filterTag.POD_ALL_CLIENTS,
  filterTag.POD_SIX_PLUS_DAYS_WITHOUT_CHECK_INS,
  filterTag.POD_NO_CHAT_2_BUSINESS_DAYS,
  filterTag.POD_ALL_DISENGAGED,
];

const CHECK_IN_NOT_SUBMITTED_DAYS = 6;
// Standalone chat-silence broadcast filter: clients who have not chatted in this many business days.
// Independent of the disengaged/check-in filters — based solely on chat inactivity.
const NO_CHAT_BUSINESS_DAYS = 2;
const MAX_MESSAGE_LENGTH = 1000;

// Module scope so the recipient rows share one handler instead of allocating a closure per client.
// Some rosters hold HEIC avatars, which Chrome downloads but cannot decode; swap in the placeholder
// rather than leaving an empty avatar box.
const handleAvatarError = (event) => {
  const image = event.target;
  if (image.dataset.fallbackApplied) {
    return;
  }
  image.dataset.fallbackApplied = 'true';
  image.src = defaultUser;
};

const RECIPIENT_MODE = {
  BY_TAG: 'BY_TAG',
  BY_CLIENT: 'BY_CLIENT',
};

const MessagingBroadcastModal = ({
  isOpen,
  onDidDismiss,
  showMessageError,
  setIsModalOpen,
}) => {
  const {
    coachDoc,
    products,
    isProductsReady,
  } = useContext(ExternalCoachContext);
  const timezoneOffset = getUTCOffset(coachDoc.timeZone || moment.tz.guess());
  // Modal states
  const [isReady, setIsReady] = useState(false);
  const [productCategories, setProductCategories] = useState(new Map());
  const [tabSelected, setTabSelected] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Recipient mode
  const [recipientMode, setRecipientMode] = useState(RECIPIENT_MODE.BY_TAG);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  // Broadcast message values
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sendDate, setSendDate] = useState(moment().utcOffset(timezoneOffset));
  const [sendTime, setSendTime] = useState(roundToNext5Minutes(moment().utcOffset(timezoneOffset)));
  const [fileAttachments, setFileAttachments] = useState([]);
  const [audioAttachment, setAudioAttachment] = useState(null);
  // Delivery
  const [deliveryMode, setDeliveryMode] = useState(DELIVERY_MODE.SEND_NOW);
  // Recipients states
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedClientIds, setSelectedClientIds] = useState(new Set());
  const [excludedClientIds, setExcludedClientIds] = useState(new Set());
  const [filterTags, setFilterTags] = useState([filterTag.ALL]);
  const [recipients, setRecipients] = useState([]);
  const [shouldForceSend, setShouldForceSend] = useState(false);
  const [shouldChangeRecipientAtSendTime, setShouldChangeRecipientAtSendTime] = useState(false);
  const [excludedTags, setExcludedTags] = useState(new Set());
  const [isExcludeDrawerOpen, setIsExcludeDrawerOpen] = useState(false);
  const [excludeTagSearchQuery, setExcludeTagSearchQuery] = useState('');

  const {
    uploadAttachments,
    deleteAttachments,
  } = useStorage();
  const {
    sendBroadcastMessage,
    scheduleBroadcastMessage,
    broadcastInitialFilters,
    setBroadcastInitialFilters,
  } = useContext(ChatContext);
  const {
    userId: loggedInUserId,
  } = useContext(LoggedInUserContext);
  const { selectedPodId } = useContext(PodContext);
  const thresholds = useDisengagementThresholds();
  const { isCoachAssistant } = useSessionStore();
  const showDisengagementFilters = isCoachAssistant;
  const isComponentMountedRef = useComponentMounted();
  const { userId } = useContext(UserContext);
  const { logCoachingActivity, logEvent } = useLogger();

  const { showToast } = useToast();

  // Capture initial filters in a ref so init() can read them without re-triggering the effect
  const initialFiltersRef = useRef(broadcastInitialFilters);
  initialFiltersRef.current = broadcastInitialFilters;

  useEffect(() => {
    const init = async () => {
      const usersListOfCoach = await Coach.getBroadcastMessageUsers(userId);
      let coachProducts = [];
      if (isProductsReady) {
        coachProducts = products;
      } else {
        const productsCol = await Product.getProductByCoachId(userId);
        coachProducts = productsCol.docs;
      }
      const chatEnabledUserList = getGivenFeatureEnabledUsersList(usersListOfCoach, Features.CHAT, coachProducts);
      const sortedUsersListOfCoach = [...chatEnabledUserList].sort((a, b) => (a.name > b.name ? 1 : -1));

      let hasPodClients = false;
      let hasUsersWithoutCheckIns = false;
      let hasPodUsersWithoutCheckIns = false;
      let hasUsersWithoutChat = false;
      let hasPodUsersWithoutChat = false;
      const results = sortedUsersListOfCoach.map((user) => {
        // Default to enabled when the product doesn't list the feature
        const checkInEnabled = user.featureProductIds.some(
          (productId) => isProductHasEnabledGivenFeature(
            productId,
            Features.CHECK_IN_NOTIFICATION,
            coachProducts,
            true,
          ),
        );

        if (!hasPodClients && user.userPod === selectedPodId) {
          hasPodClients = true;
        }

        if ((!hasUsersWithoutCheckIns || !hasPodUsersWithoutCheckIns) && user.lastCheckIn) {
          const daysSinceLastCheckIn = moment().diff(moment(user.lastCheckIn), 'days');
          if (daysSinceLastCheckIn > CHECK_IN_NOT_SUBMITTED_DAYS) {
            if (user.userPod === selectedPodId) {
              hasPodUsersWithoutCheckIns = true;
            }
            hasUsersWithoutCheckIns = true;
          }
        }

        if ((!hasUsersWithoutChat || !hasPodUsersWithoutChat)
          && matchesChatSilenceSegment(user, NO_CHAT_BUSINESS_DAYS)) {
          if (user.userPod === selectedPodId) {
            hasPodUsersWithoutChat = true;
          }
          hasUsersWithoutChat = true;
        }
        return {
          tags: [...user.tags, ...user.customTags],
          clientData: {
            id: user.id,
            userDoc: user,
            userTags: [...user.tags, ...user.customTags],
            features: { checkInEnabled },
          },
        };
      });

      const tagList = [...new Set(results.flatMap((result) => result.tags))];
      const clientList = results.reduce((userArr, result) => {
        if (result.clientData) {
          return [...userArr, result.clientData];
        }
        return userArr;
      }, []);

      if (isComponentMountedRef.current) {
        // The category comes off the product now; these are the same docs the feature checks above already read.
        setProductCategories(categoryMap(coachProducts));
        const finalFilterTags = [
          filterTag.ALL,
          ...(hasPodClients ? [filterTag.POD_ALL_CLIENTS] : []),
          ...(hasUsersWithoutCheckIns ? [filterTag.SIX_PLUS_DAYS_WITHOUT_CHECK_INS] : []),
          ...(hasPodUsersWithoutCheckIns ? [filterTag.POD_SIX_PLUS_DAYS_WITHOUT_CHECK_INS] : []),
          ...(hasUsersWithoutChat ? [filterTag.NO_CHAT_2_BUSINESS_DAYS] : []),
          ...(hasPodUsersWithoutChat ? [filterTag.POD_NO_CHAT_2_BUSINESS_DAYS] : []),
          ...tagList,
        ];
        setFilterTags(finalFilterTags);
        setRecipients(clientList);

        // Apply initial filter from the disengaged feed if present
        const currentInitialFilters = initialFiltersRef.current;
        if (currentInitialFilters && currentInitialFilters.length > 0) {
          if (showDisengagementFilters && currentInitialFilters.includes(DISENGAGEMENT_BROADCAST_TAG)) {
            // The disengaged feed is pod-filtered, so pre-select the pod-scoped tag when a pod
            // is active to match the clients the AC was just looking at.
            const isPodActive = selectedPodId && selectedPodId !== ALL_CLIENTS;
            setSelectedTags([isPodActive ? filterTag.POD_ALL_DISENGAGED : filterTag.ALL_DISENGAGED]);
          }
          setBroadcastInitialFilters(null);
        }

        setIsReady(true);
      }
    };

    if (!isReady) {
      init();
    }
  }, [
    isComponentMountedRef,
    userId,
    isReady,
    isProductsReady,
    products,
    selectedPodId,
    isCoachAssistant,
    showDisengagementFilters,
    setBroadcastInitialFilters,
  ]);

  // Count clients per tag for the By Tag list
  const tagClientCounts = useMemo(() => {
    const counts = {};
    counts[filterTag.ALL] = recipients.length;
    recipients.forEach((r) => {
      (r.userTags || []).forEach((tag) => {
        counts[tag] = (counts[tag] || 0) + 1;
      });
      if (r.userDoc.userPod === selectedPodId) {
        counts[filterTag.POD_ALL_CLIENTS] = (counts[filterTag.POD_ALL_CLIENTS] || 0) + 1;
      }
      if (r.userDoc.lastCheckIn) {
        const days = moment().diff(moment(r.userDoc.lastCheckIn), 'days');
        if (days > CHECK_IN_NOT_SUBMITTED_DAYS) {
          counts[filterTag.SIX_PLUS_DAYS_WITHOUT_CHECK_INS] = (
            counts[filterTag.SIX_PLUS_DAYS_WITHOUT_CHECK_INS] || 0
          ) + 1;
          if (r.userDoc.userPod === selectedPodId) {
            counts[filterTag.POD_SIX_PLUS_DAYS_WITHOUT_CHECK_INS] = (
              counts[filterTag.POD_SIX_PLUS_DAYS_WITHOUT_CHECK_INS] || 0
            ) + 1;
          }
        }
      }
      // Count chat-silent clients (no chat from client or coach in 2+ business days)
      if (matchesChatSilenceSegment(r.userDoc, NO_CHAT_BUSINESS_DAYS)) {
        counts[filterTag.NO_CHAT_2_BUSINESS_DAYS] = (counts[filterTag.NO_CHAT_2_BUSINESS_DAYS] || 0) + 1;
        if (r.userDoc.userPod === selectedPodId) {
          counts[filterTag.POD_NO_CHAT_2_BUSINESS_DAYS] = (
            counts[filterTag.POD_NO_CHAT_2_BUSINESS_DAYS] || 0
          ) + 1;
        }
      }
      // Count disengaged clients
      const { userDoc, features } = r;
      if (isDisengagementEligible(userDoc, productCategories, userId)
        && isClientDisengaged(userDoc, features, thresholds)) {
        counts[filterTag.ALL_DISENGAGED] = (counts[filterTag.ALL_DISENGAGED] || 0) + 1;
        if (userDoc.userPod === selectedPodId) {
          counts[filterTag.POD_ALL_DISENGAGED] = (counts[filterTag.POD_ALL_DISENGAGED] || 0) + 1;
        }
      }
    });
    return counts;
  }, [recipients, selectedPodId, thresholds, productCategories, userId]);

  // The disengaged tags are offered reactively based on the live recipient counts, so they stay
  // correct regardless of when the PostHog thresholds payload resolves. The roster-wide tag shows
  // whenever any client is disengaged; the pod-scoped tag shows only when the selected pod has one
  // (its count is 0 under "All clients", mirroring the "in this pod" check-in tag).
  const offeredFilterTags = useMemo(() => {
    if (!showDisengagementFilters) return filterTags;
    const disengagedTagsToAdd = [
      filterTag.ALL_DISENGAGED,
      filterTag.POD_ALL_DISENGAGED,
    ].filter((tag) => (tagClientCounts[tag] || 0) > 0 && !filterTags.includes(tag));
    if (disengagedTagsToAdd.length === 0) return filterTags;
    // Place the disengaged tags after the system check-in tags (ALL + the check-in tags)
    // but before the coach's custom tags.
    const systemTags = [
      filterTag.ALL,
      filterTag.SIX_PLUS_DAYS_WITHOUT_CHECK_INS,
      filterTag.POD_SIX_PLUS_DAYS_WITHOUT_CHECK_INS,
      filterTag.NO_CHAT_2_BUSINESS_DAYS,
      filterTag.POD_NO_CHAT_2_BUSINESS_DAYS,
    ];
    const lastSystemIndex = filterTags.reduce(
      (acc, tag, index) => (systemTags.includes(tag) ? index : acc),
      -1,
    );
    const result = [...filterTags];
    result.splice(lastSystemIndex + 1, 0, ...disengagedTagsToAdd);
    return result;
  }, [filterTags, showDisengagementFilters, tagClientCounts]);

  // Filtered tag list for By Tag mode
  const filteredTagsList = useMemo(() => {
    const query = tagSearchQuery.toLowerCase();
    return offeredFilterTags
      .filter((tag) => tag !== filterTag.ALL)
      .filter((tag) => tag.toLowerCase().includes(query))
      .map((tag) => ({
        id: tag,
        label: tag,
        count: tagClientCounts[tag] || 0,
      }));
  }, [offeredFilterTags, tagSearchQuery, tagClientCounts]);

  // Filtered tag list for the exclude drawer
  const filteredExcludeTagsList = useMemo(() => {
    const query = excludeTagSearchQuery.toLowerCase();
    return offeredFilterTags
      .filter((tag) => tag !== filterTag.ALL)
      .filter((tag) => tag.toLowerCase().includes(query))
      .map((tag) => ({
        id: tag,
        label: tag,
        count: tagClientCounts[tag] || 0,
      }));
  }, [offeredFilterTags, excludeTagSearchQuery, tagClientCounts]);

  // Filtered client list for By Client mode
  const filteredClientsList = useMemo(() => {
    const query = clientSearchQuery.toLowerCase();
    return recipients.filter((r) => r.userDoc.name.toLowerCase().includes(query));
  }, [recipients, clientSearchQuery]);

  // Returns true if a recipient matches a given tag — shared by include and exclude logic
  const clientMatchesTag = useCallback((r, tag) => {
    if (tag === filterTag.ALL) return true;
    if (tag === filterTag.POD_ALL_CLIENTS) return r.userDoc.userPod === selectedPodId;
    if (tag === filterTag.SIX_PLUS_DAYS_WITHOUT_CHECK_INS) {
      if (!r.userDoc.lastCheckIn) return false;
      return moment().diff(moment(r.userDoc.lastCheckIn), 'days') > CHECK_IN_NOT_SUBMITTED_DAYS;
    }
    if (tag === filterTag.POD_SIX_PLUS_DAYS_WITHOUT_CHECK_INS) {
      if (!r.userDoc.lastCheckIn) return false;
      return moment().diff(moment(r.userDoc.lastCheckIn), 'days') > CHECK_IN_NOT_SUBMITTED_DAYS
        && r.userDoc.userPod === selectedPodId;
    }
    if (tag === filterTag.NO_CHAT_2_BUSINESS_DAYS) {
      return matchesChatSilenceSegment(r.userDoc, NO_CHAT_BUSINESS_DAYS);
    }
    if (tag === filterTag.POD_NO_CHAT_2_BUSINESS_DAYS) {
      return matchesChatSilenceSegment(r.userDoc, NO_CHAT_BUSINESS_DAYS)
        && r.userDoc.userPod === selectedPodId;
    }
    if (tag === filterTag.ALL_DISENGAGED) {
      if (!isDisengagementEligible(r.userDoc, productCategories, userId)) return false;
      return isClientDisengaged(r.userDoc, r.features, thresholds);
    }
    if (tag === filterTag.POD_ALL_DISENGAGED) {
      if (!isDisengagementEligible(r.userDoc, productCategories, userId)) return false;
      if (!isClientDisengaged(r.userDoc, r.features, thresholds)) return false;
      return r.userDoc.userPod === selectedPodId;
    }
    return r.userTags?.includes(tag);
  }, [selectedPodId, thresholds, productCategories, userId]);

  // Compute recipients from selected tags
  const recipientsFromTags = useMemo(() => {
    if (!selectedTags || selectedTags.length === 0) return [];
    return recipients.filter((r) => selectedTags.some((tag) => clientMatchesTag(r, tag)));
  }, [selectedTags, recipients, clientMatchesTag]);

  // IDs of recipients who match any excluded tag
  const recipientsExcludedByTags = useMemo(() => {
    if (excludedTags.size === 0) return new Set();
    return new Set(
      recipients
        .filter((r) => [...excludedTags].some((tag) => clientMatchesTag(r, tag)))
        .map((r) => r.id),
    );
  }, [excludedTags, recipients, clientMatchesTag]);

  // Union of tag-selected and individually-selected clients, minus any explicitly excluded
  const filteredRecipients = useMemo(() => {
    const tagClientIds = new Set(recipientsFromTags.map((r) => r.id));
    const isExcluded = (r) => excludedClientIds.has(r.id) || recipientsExcludedByTags.has(r.id);
    const filteredTagClients = recipientsFromTags.filter((r) => !isExcluded(r));
    const extraClients = recipients.filter(
      (r) => selectedClientIds.has(r.id) && !tagClientIds.has(r.id) && !isExcluded(r),
    );
    return [...filteredTagClients, ...extraClients];
  }, [recipientsFromTags, recipients, selectedClientIds, excludedClientIds, recipientsExcludedByTags]);

  // Selected ids as a Set so membership checks stay O(1). Every "is this client selected?" lookup
  // reads this — a linear scan per row turns the list render quadratic on large rosters.
  const selectedRecipientIds = useMemo(() => new Set(filteredRecipients.map((r) => r.id)), [filteredRecipients]);

  // True when the broadcast effectively targets every active client, regardless of whether they
  // were chosen by tag, by client, or via the ALL tag. Derived (never hand-synced) so it stays
  // correct across every selection change, and exclusion-aware so removing anyone makes it false.
  const isEffectivelyAllClients = useMemo(() => {
    if (recipients.length === 0) return false;
    return recipients.length === selectedRecipientIds.size && recipients.every((r) => selectedRecipientIds.has(r.id));
  }, [recipients, selectedRecipientIds]);

  // The "Disengaged clients" option is a synthetic segment computed client-side, not a real user tag.
  // Used to tag scheduled disengaged broadcasts as targeted outreach (see handleBroadcastSchedule) so
  // the server grants the outreach cooldown and clears the disengaged activity after delivery.
  const hasDisengagedTagSelected = selectedTags.some((tag) => disengagementFilterTags.includes(tag));

  // Client list sorted so selected clients appear first
  const sortedFilteredClientsList = useMemo(() => (
    [...filteredClientsList].sort((a, b) => {
      const aSelected = selectedRecipientIds.has(a.id);
      const bSelected = selectedRecipientIds.has(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    })
  ), [filteredClientsList, selectedRecipientIds]);

  const filterList = useCallback((newSelectedTags) => {
    setSelectedTags(newSelectedTags || []);
  }, []);

  const handleTagToggle = useCallback((tag) => {
    const newSelectedTags = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    filterList(newSelectedTags);
  }, [selectedTags, filterList]);

  const handleExcludeTagToggle = useCallback((tag) => {
    setExcludedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }, []);

  const handleClientToggle = useCallback((checked, id) => {
    // First client interaction after tag selections: commit the computed list to
    // explicit selections, clear tag state, then apply this toggle on top.
    if (selectedTags.length > 0 || excludedTags.size > 0) {
      const next = new Set(selectedRecipientIds);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      setSelectedClientIds(next);
      setSelectedTags([]);
      setExcludedTags(new Set());
      setExcludedClientIds(new Set());
      return;
    }

    const isFromTag = recipientsFromTags.some((r) => r.id === id);
    if (isFromTag) {
      setExcludedClientIds((prev) => {
        const next = new Set(prev);
        if (!checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    } else {
      setSelectedClientIds((prev) => {
        const next = new Set(prev);
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    }
  }, [selectedTags, excludedTags, selectedRecipientIds, recipientsFromTags]);

  const isAllTagsSelected = filteredTagsList.length > 0 && filteredTagsList.every((t) => selectedTags.includes(t.id));
  const isAllClientsSelected = filteredClientsList.length > 0
    && filteredClientsList.every((r) => selectedRecipientIds.has(r.id));

  const handleSelectAll = useCallback(() => {
    if (recipientMode === RECIPIENT_MODE.BY_TAG) {
      if (isAllTagsSelected) {
        filterList(selectedTags.filter((id) => !filteredTagsList.some((t) => t.id === id)));
      } else {
        const filteredIds = filteredTagsList.map((t) => t.id);
        filterList([...new Set([...selectedTags, ...filteredIds])]);
      }
    } else if (isAllClientsSelected) {
      setSelectedClientIds(new Set());
      setSelectedTags([]);
      setExcludedTags(new Set());
      setExcludedClientIds(new Set());
    } else {
      setSelectedClientIds(new Set(filteredClientsList.map((r) => r.id)));
      setSelectedTags([]);
      setExcludedTags(new Set());
      setExcludedClientIds(new Set());
    }
  }, [
    recipientMode,
    isAllTagsSelected,
    isAllClientsSelected,
    filteredTagsList,
    filteredClientsList,
    filterList,
    selectedTags,
  ]);

  const resetFields = useCallback(() => {
    setBroadcastMessage('');
    setSelectedTags([]);
    setSelectedClientIds(new Set());
    setExcludedClientIds(new Set());
    setExcludedTags(new Set());
    setSendDate(moment().local());
    setSendTime(moment().local());
    setFileAttachments([]);
    setAudioAttachment(null);
    setTagSearchQuery('');
    setClientSearchQuery('');
    setExcludeTagSearchQuery('');
    setIsExcludeDrawerOpen(false);
    setShouldChangeRecipientAtSendTime(false);
    setDeliveryMode(DELIVERY_MODE.SEND_NOW);
  }, []);

  const getFileAttachments = useCallback(async () => {
    const fileAttachmentsData = {};
    if (fileAttachments.length || audioAttachment) {
      setIsUploading(true);

      const storagePath = format(storagePaths.COACH_BROADCAST_MESSAGES_ATTACHMENTS, {
        [pathPlaceholder.COACH_ID]: userId,
      });

      const { storageTaskFailed, attachmentsRefs } = await uploadMessageAttachments({
        fileAttachments,
        audioAttachment,
        userId,
        uploadAttachments,
        deleteAttachments,
        storagePath,
      });
      setIsUploading(false);

      if (storageTaskFailed) {
        setFileAttachments([]);
        setAudioAttachment(null);
        return { error: true };
      }
      fileAttachmentsData.attachmentsRefs = attachmentsRefs;
    }
    return { fileAttachmentsData, error: false };
  }, [
    audioAttachment,
    fileAttachments,
    userId,
    uploadAttachments,
    deleteAttachments,
  ]);

  const checkDateTime = useCallback(() => {
    if (!moment(sendTime, 'HH:mm', true).isValid() || !moment(sendDate).isValid()) {
      showToast(`${text.invalidDateTime}`, { error: true });
      return false;
    }
    const sendAt = combineDateAndTimeWithUTCOffset(sendDate, sendTime, timezoneOffset);
    if (sendAt.isBefore(moment())) {
      showToast(`${text.pastDateTime}`, { error: true });
      return false;
    }
    return true;
  }, [sendDate, sendTime, showToast, timezoneOffset]);

  const handleBroadcastSend = useCallback(async () => {
    const { fileAttachmentsData, error } = await getFileAttachments();
    if (error) {
      showMessageError(text.uploadError);
      return;
    }
    if (isComponentMountedRef.current) {
      setIsLoading(true);
    }
    const activeDisengagementFilters = selectedTags.filter((f) => disengagementFilterTags.includes(f));
    const outreachFields = activeDisengagementFilters.length > 0
      ? { outreachType: OutreachType.BROADCAST_TARGETED } : {};
    await sendBroadcastMessage(
      filteredRecipients,
      broadcastMessage,
      {
        ...fileAttachmentsData,
        ...outreachFields,
        selectedTags,
        excludedTags: [...excludedTags],
      },
      shouldForceSend,
    );
    if (activeDisengagementFilters.length > 0) {
      logEvent('disengaged_broadcast_sent', {
        ac_id: userId,
        disengagement_criteria: activeDisengagementFilters,
        recipient_count: filteredRecipients.length,
        outreach_type: OutreachType.BROADCAST_TARGETED,
      });
    }
    resetFields();
    showToast(text.broadcastMessage);
    onDidDismiss();
    if (isComponentMountedRef.current) {
      setIsLoading(false);
    }
  }, [
    broadcastMessage,
    filteredRecipients,
    resetFields,
    showMessageError,
    sendBroadcastMessage,
    getFileAttachments,
    onDidDismiss,
    showToast,
    isComponentMountedRef,
    shouldForceSend,
    excludedTags,
    selectedTags,
    logEvent,
    userId,
  ]);

  const handleBroadcastSchedule = useCallback(async () => {
    const { fileAttachmentsData, error } = await getFileAttachments();
    if (error) {
      showMessageError(text.uploadError);
      return;
    }
    if (!checkDateTime()) {
      return;
    }
    if (isComponentMountedRef.current) {
      setIsLoading(true);
    }
    const sendAt = combineDateAndTimeWithUTCOffset(sendDate, sendTime, timezoneOffset).valueOf();
    let recipientsChangingTags = null;
    if (shouldChangeRecipientAtSendTime) {
      /*
       * Only write ['ALL'] for a true "all clients" selection (no tags). In BY_TAG mode a tag may
       * happen to cover everyone, but the intent is still tag-scoped — write selectedTags so the
       * server re-queries by tag at send time and only matching new clients are included. This keeps
       * the written value consistent with the "matching clients" label shown in that case.
       *
       * The disengaged tags are re-queried server-side too (S2-8112): functions-22
       * getNewRecipientsFromTags resolves them via the canonical evaluateDisengagement model, so
       * they are safe to forward and are no longer treated as synthetic-and-unresolvable.
       */
      recipientsChangingTags = (isEffectivelyAllClients && selectedTags.length === 0)
        ? [filterTag.ALL]
        : selectedTags;
    }

    // Mirror the send-now path: tag scheduled disengaged broadcasts as targeted outreach so the
    // server grants the outreach cooldown and clears the disengaged activity after delivery.
    const outreachFields = hasDisengagedTagSelected
      ? { outreachType: OutreachType.BROADCAST_TARGETED }
      : {};

    /*
     * The server needs selectedPodId to resolve any pod-scoped tag at send time — on the include
     * side and on the exclude side alike (without it, user.podId === undefined matches nobody and
     * the exclusion silently excludes no one). ALL only short-circuits the include side: when
     * everyone is selected the pod-scoped include tag is redundant, but excluding a pod-scoped tag
     * out of "all clients" is a legitimate selection that still needs the pod id.
     */
    const needsPodId = shouldChangeRecipientAtSendTime && (
      [...excludedTags].some((tag) => podScopedFilterTags.includes(tag))
      || (!selectedTags.includes(filterTag.ALL)
        && selectedTags.some((tag) => podScopedFilterTags.includes(tag)))
    );

    await scheduleBroadcastMessage({
      recipients: filteredRecipients,
      message: broadcastMessage,
      sendAt,
      shouldForceSend,
      isCheckInReminder: !!selectedTags.includes(filterTag.SIX_PLUS_DAYS_WITHOUT_CHECK_INS),
      recipientsChangingTags,
      selectedPodId: needsPodId ? selectedPodId : null,
      fileAttachmentsData,
      selectedTags,
      excludedTags: [...excludedTags],
      ...outreachFields,
    });

    logCoachingActivity(CoachingActivity.SCHEDULED_BROADCAST_MESSAGE, {
      recipients: filteredRecipients.map((recipient) => recipient.id),
      sendAt,
      scheduledBy: loggedInUserId,
      coach: userId,
    });
    resetFields();
    showToast(text.broadcastMessageScheduled);
    onDidDismiss();
    setIsModalOpen(true);
    setTabSelected(1);
    if (isComponentMountedRef.current) {
      setIsLoading(false);
    }
  }, [
    sendDate,
    sendTime,
    broadcastMessage,
    filteredRecipients,
    resetFields,
    showMessageError,
    scheduleBroadcastMessage,
    getFileAttachments,
    setIsModalOpen,
    onDidDismiss,
    checkDateTime,
    logCoachingActivity,
    loggedInUserId,
    userId,
    timezoneOffset,
    showToast,
    isComponentMountedRef,
    selectedTags,
    excludedTags,
    isEffectivelyAllClients,
    shouldForceSend,
    shouldChangeRecipientAtSendTime,
    selectedPodId,
    hasDisengagedTagSelected,
  ]);

  const onFilesSelected = useCallback((event) => {
    const { files } = event.target;
    if (files.length) {
      const filesArray = [...files];
      const { hasUnsupportedFiles, filteredFiles } = filterUnsupportedFiles(filesArray);
      if (hasUnsupportedFiles) {
        const fileTypes = getUnsupportedFileTypes();
        showMessageError(format(text.unsupportedFileWarning, { fileTypes }));
      }
      if (!filteredFiles.length) {
        return;
      }
      const validFiles = filteredFiles.every((file) => file.size < MAX_FILE_UPLOAD_SIZE);
      if (validFiles) {
        setFileAttachments(filteredFiles);
      } else {
        showMessageError(format(text.fileSizeError, {
          maxFileSize: MAX_FILE_UPLOAD_SIZE_MB,
        }));
      }
    }
  }, [showMessageError]);

  const onAudioRecorded = useCallback((audioFile) => {
    setAudioAttachment(audioFile);
  }, []);

  const removeFileByIndex = useCallback((index) => {
    const resultAttachments = [...fileAttachments];
    resultAttachments.splice(index, 1);
    setFileAttachments(resultAttachments);
  }, [fileAttachments]);

  const removeAudioAttachment = useCallback(() => {
    setAudioAttachment(null);
  }, []);

  const cursorPosRef = useRef(null);

  const setEmoji = useCallback((e) => {
    setBroadcastMessage((prevText) => {
      const pos = cursorPosRef.current ?? (prevText?.length ?? 0);
      return `${prevText?.slice(0, pos) ?? ''}${e.emoji}${prevText?.slice(pos) ?? ''}`;
    });
    cursorPosRef.current = (cursorPosRef.current ?? 0) + e.emoji.length;
  }, []);

  const minTime = useMemo(() => (
    isToday(moment(sendDate).utcOffset(timezoneOffset)) ? moment().utcOffset(timezoneOffset) : null
  ), [sendDate, timezoneOffset]);

  const onTabChange = useCallback((_, value) => {
    setTabSelected(value);
  }, []);

  const a11yProps = useCallback((index) => ({
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  }), []);

  const extraClientCount = filteredRecipients.length - recipientsFromTags.length;
  const showMixedSelectionHint = selectedTags.length > 0 && extraClientCount > 0;

  const isSendDisabled = filteredRecipients.length === 0 || !broadcastMessage;
  const isMessageOverLimit = broadcastMessage.length > MAX_MESSAGE_LENGTH;

  const footerButtonText = filteredRecipients.length > 0
    ? format(
      deliveryMode === DELIVERY_MODE.SCHEDULE ? text.scheduleToRecipients : text.sendToRecipients,
      { count: filteredRecipients.length },
    )
    : text.selectRecipientsToSend;

  const isAllSelected = recipientMode === RECIPIENT_MODE.BY_TAG ? isAllTagsSelected : isAllClientsSelected;

  const handleSendOrSchedule = useCallback(() => {
    if (deliveryMode === DELIVERY_MODE.SCHEDULE) {
      handleBroadcastSchedule();
    } else {
      handleBroadcastSend();
    }
  }, [deliveryMode, handleBroadcastSend, handleBroadcastSchedule]);

  // Memoized so unrelated state — most importantly every keystroke in the message body —
  // does not re-render and reconcile one row (and one MUI Checkbox) per client.
  const recipientsSection = useMemo(() => {
    if (!isReady) {
      return (
        <IonItem>
          <IonSpinner />
        </IonItem>
      );
    }

    if (recipientMode === RECIPIENT_MODE.BY_TAG) {
      return (
        <StyledListContainer>
          {filteredTagsList.length === 0 ? (
            <RecipientRow>
              <RecipientInfo>
                <RecipientName>{text.noTagsFound}</RecipientName>
              </RecipientInfo>
            </RecipientRow>
          ) : (
            filteredTagsList.map((tag) => (
              <RecipientRow key={tag.id} onClick={() => handleTagToggle(tag.id)}>
                <RecipientInfo>
                  <RecipientName>{tag.label}</RecipientName>
                  <RecipientSubText>
                    {format(text.clients, { count: tag.count })}
                  </RecipientSubText>
                  {excludedTags.has(tag.id) && (
                    <ConflictWarning>{text.excludeConflictWarning}</ConflictWarning>
                  )}
                </RecipientInfo>
                <Checkbox
                  checked={selectedTags.includes(tag.id)}
                  onChange={() => handleTagToggle(tag.id)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
              </RecipientRow>
            ))
          )}
        </StyledListContainer>
      );
    }

    // BY_CLIENT mode
    if (filteredClientsList.length === 0) {
      return (
        <StyledListContainer>
          <RecipientRow>
            <RecipientInfo>
              <RecipientName>{recipients.length === 0 ? text.noActiveClients : text.noClientsFound}</RecipientName>
            </RecipientInfo>
          </RecipientRow>
        </StyledListContainer>
      );
    }

    return (
      <StyledListContainer>
        {sortedFilteredClientsList.map((recipient) => {
          const isChecked = selectedRecipientIds.has(recipient.id);
          return (
            <RecipientRow
              key={recipient.id}
              onClick={() => handleClientToggle(!isChecked, recipient.id)}
            >
              <StyledAvatar
                slot="start"
              >
                {/* Avatars are original camera uploads (~1 MB each, some HEIC that Chrome cannot
                    decode) shown at 28px, and only ~4 rows are ever visible. Deferring the fetch
                    and the decode keeps a large roster from mounting hundreds of MB at once;
                    a failed load falls back to the placeholder instead of an empty box. */}
                <img
                  src={recipient.userDoc.avatarUrl ? recipient.userDoc.avatarUrl : defaultUser}
                  alt={recipient.userDoc.name}
                  width={AVATAR_PIXEL_SIZE}
                  height={AVATAR_PIXEL_SIZE}
                  loading="lazy"
                  decoding="async"
                  onError={handleAvatarError}
                />
              </StyledAvatar>
              <RecipientInfo>
                <RecipientName>{recipient.userDoc.name}</RecipientName>
              </RecipientInfo>
              <Checkbox
                checked={isChecked}
                onChange={(e) => handleClientToggle(e.target.checked, recipient.id)}
                onClick={(ev) => ev.stopPropagation()}
                size="small"
              />
            </RecipientRow>
          );
        })}
      </StyledListContainer>
    );
  }, [
    isReady,
    recipientMode,
    filteredTagsList,
    selectedTags,
    excludedTags,
    handleTagToggle,
    filteredClientsList,
    sortedFilteredClientsList,
    selectedRecipientIds,
    handleClientToggle,
    recipients.length,
  ]);

  return (
    <>
      <DialogRoundedModal
        open={isOpen}
        onClose={onDidDismiss}
        title={text.broadcastMessageTitle}
        description={text.broadcastMessageSubtitle}
        maxWidth="md"
        fullWidth
        actionButtons={tabSelected === 0 ? (
          <SendBroadcastButton
            onClick={handleSendOrSchedule}
            disabled={isSendDisabled || isMessageOverLimit}
          >
            {footerButtonText}
          </SendBroadcastButton>
        ) : null}
      >
        <StyledTabs value={tabSelected} onChange={onTabChange}>
          <StyledTab label={text.newBroadcast} {...a11yProps(0)} icon={<PostAdd />} />
          <StyledTab label={text.scheduledBroadcasts} {...a11yProps(1)} icon={<Business />} />
        </StyledTabs>
        <ModalBody>
          <TabPanel index={0} value={tabSelected}>
            <TabContainer>

              {/* 1. Recipients Section */}
              <SectionContainer>
                <SectionLabel>{text.recipientsLabel}</SectionLabel>
                <StyledToggleButtonGroup
                  value={recipientMode}
                  exclusive
                  onChange={(_, value) => value && setRecipientMode(value)}
                  size="small"
                >
                  <ToggleButton value={RECIPIENT_MODE.BY_TAG}>{text.byTag}</ToggleButton>
                  <ToggleButton value={RECIPIENT_MODE.BY_CLIENT}>{text.byClient}</ToggleButton>
                </StyledToggleButtonGroup>

                {recipientMode === RECIPIENT_MODE.BY_TAG && (
                  <HelperText>{text.byTagHint}</HelperText>
                )}

                {showMixedSelectionHint && (
                  <HelperText>
                    {format(text.mixedSelectionBreakdown, {
                      tagCount: recipientsFromTags.length,
                      extraCount: extraClientCount,
                    })}
                  </HelperText>
                )}

                <SearchBar
                  size="small"
                  placeholder={recipientMode === RECIPIENT_MODE.BY_TAG ? text.searchTags : text.searchClients}
                  value={recipientMode === RECIPIENT_MODE.BY_TAG ? tagSearchQuery : clientSearchQuery}
                  onChange={(e) => (recipientMode === RECIPIENT_MODE.BY_TAG
                    ? setTagSearchQuery(e.target.value)
                    : setClientSearchQuery(e.target.value))}
                />

                <SelectAllButton
                  onClick={handleSelectAll}
                  disabled={recipientMode === RECIPIENT_MODE.BY_TAG
                    ? filteredTagsList.length === 0
                    : filteredClientsList.length === 0}
                >
                  {isAllSelected ? text.deselectAll : text.selectAll}
                </SelectAllButton>

                {recipientsSection}

                {recipientMode === RECIPIENT_MODE.BY_TAG && (
                  <ExcludeTagsDrawer
                    excludedTags={excludedTags}
                    onTagToggle={handleExcludeTagToggle}
                    tagsList={filteredExcludeTagsList}
                    searchQuery={excludeTagSearchQuery}
                    onSearchQueryChange={setExcludeTagSearchQuery}
                    isOpen={isExcludeDrawerOpen}
                    onToggleOpen={() => setIsExcludeDrawerOpen((prev) => !prev)}
                    removedCount={recipientsFromTags.filter((r) => recipientsExcludedByTags.has(r.id)).length}
                    includedCount={recipientsFromTags.length}
                  />
                )}
              </SectionContainer>

              {/* 2. Message Section */}
              <SectionContainer>
                <SectionLabel>{text.messageLabel}</SectionLabel>
                <PersonalizeChipsContainer>
                  <PersonalizeChip
                    label={text.personalizeClientName}
                    onClick={() => setBroadcastMessage((prev) => `${prev}{clientName}`)}
                    size="small"
                    variant="outlined"
                  />
                </PersonalizeChipsContainer>
                <StyledTextField
                  placeholder={text.enterMessage}
                  value={broadcastMessage}
                  rows={4}
                  multiline
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  onBlur={(e) => { cursorPosRef.current = e.target.selectionStart; }}
                  error={isMessageOverLimit}
                  helperText={isMessageOverLimit ? format(text.messageTooLong, { maxLength: MAX_MESSAGE_LENGTH }) : ''}
                />
                <MessageToolbar>
                  <EmojiInput onEmojiClick={setEmoji} />
                </MessageToolbar>
                <AttachmentsWrapper>
                  <AttachmentsContainer
                    fileAttachments={fileAttachments}
                    onFilesSelected={onFilesSelected}
                    onFileRemoved={removeFileByIndex}
                    isUploading={isUploading}
                  />
                  <AudioAttachmentsContainer
                    onAudioRecorded={onAudioRecorded}
                    onRemove={removeAudioAttachment}
                    isUploading={isUploading}
                  />
                </AttachmentsWrapper>
              </SectionContainer>

              {/* 3. Delivery Section */}
              <SectionContainer>
                <SectionLabel>{text.deliveryLabel}</SectionLabel>
                <StyledToggleButtonGroup
                  value={deliveryMode}
                  exclusive
                  onChange={(_, value) => value && setDeliveryMode(value)}
                  size="small"
                >
                  <ToggleButton value={DELIVERY_MODE.SEND_NOW}>{text.sendNow}</ToggleButton>
                  <ToggleButton value={DELIVERY_MODE.SCHEDULE}>{text.schedule}</ToggleButton>
                </StyledToggleButtonGroup>

                {deliveryMode === DELIVERY_MODE.SCHEDULE && (
                  <>
                    {!!coachDoc.timeZone && (
                      <StyledAlert severity="warning">{text.scheduleWarningMessage}</StyledAlert>
                    )}
                    <DateSelectorContainer>
                      <DatePicker
                        label={text.selectDate}
                        value={sendDate}
                        onChange={(newValue) => setSendDate(newValue)}
                        minDate={moment().utcOffset(timezoneOffset)}
                        maxDate={moment().utcOffset(timezoneOffset).add(3, 'months')}
                        renderInput={(params) => <DateSelectorField {...params} size="small" />}
                      />
                      <TimePicker
                        label={text.selectTime}
                        value={sendTime}
                        onChange={(newValue) => setSendTime(newValue)}
                        minTime={minTime}
                        minutesStep={5}
                        renderInput={(params) => <DateSelectorField {...params} size="small" />}
                      />
                    </DateSelectorContainer>
                  </>
                )}
              </SectionContainer>

              {/* 4. Options Section */}
              <SectionContainer>
                <SectionLabel>{text.optionsLabel}</SectionLabel>
                <OptionsRow>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={shouldForceSend}
                        onChange={() => setShouldForceSend((prev) => !prev)}
                        size="small"
                      />
                    )}
                    label={text.forceSendLabel}
                  />
                </OptionsRow>
                <ForceSendHelperText isForceSendEnabled={shouldForceSend} />
                {(selectedTags.length > 0 || isEffectivelyAllClients) && deliveryMode === DELIVERY_MODE.SCHEDULE && (
                  <>
                    <OptionsRow>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={shouldChangeRecipientAtSendTime}
                            onChange={() => setShouldChangeRecipientAtSendTime((prev) => !prev)}
                            size="small"
                          />
                        )}
                        label={isEffectivelyAllClients && selectedTags.length === 0
                          ? text.includeNewClientsAllLabel
                          : text.includeNewClientsLabel}
                      />
                    </OptionsRow>
                    <HelperText>
                      {isEffectivelyAllClients && selectedTags.length === 0
                        ? text.includeNewClientsAllHelper
                        : text.includeNewClientsHelper}
                    </HelperText>
                  </>
                )}
              </SectionContainer>

            </TabContainer>
          </TabPanel>

          <TabPanel index={1} value={tabSelected}>
            <ScheduledMessagesList />
          </TabPanel>
        </ModalBody>
      </DialogRoundedModal>
      <LoadingOverlay isLoading={isLoading} />
    </>
  );
};

MessagingBroadcastModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDidDismiss: PropTypes.func.isRequired,
  showMessageError: PropTypes.func.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
};

export default MessagingBroadcastModal;
